import React, { useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, ArrowUp, ArrowDown, Sparkles, ListChecks, Wand2 } from 'lucide-react';
import RichTextEditor from '../RichTextEditor';
import { useNotifications } from '../../hooks/useNotifications';

export type QuizLang = 'pt' | 'en' | 'es';
type Tri<T> = Record<QuizLang, T>;

export interface QuizOption { key: string; i18n: Tri<{ label: string }> }
export interface QuizQuestion { key: string; i18n: Tri<{ text: string }>; options: QuizOption[] }
export type QuizOutcome = 'vip' | 'qualified' | 'nurture';
export interface QuizRule {
  outcome: 'vip' | 'qualified';
  conditions: { questionKey: string; optionKeys: string[] }[];
}
export interface NurtureStep { delayDays: number; i18n: Tri<{ subject: string; bodyHtml: string }> }
export interface LandingQuiz {
  enabled: boolean;
  questions: QuizQuestion[];
  rules: QuizRule[];
  outcomes: Record<QuizOutcome, { i18n: Tri<{ title: string; message: string }> }>;
  whatsappGroupUrl: string;
  nurtureSequence: NurtureStep[];
}

const LANGS: { code: QuizLang; label: string }[] = [
  { code: 'pt', label: 'Português' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' }
];

const OUTCOME_LABELS: Record<QuizOutcome, string> = {
  vip: 'VIP (ligação + grupo)',
  qualified: 'Qualificado (grupo + contato do SDR em 24 h)',
  nurture: 'Nutrição (sem grupo, recebe e-mails)'
};

const tri = <T,>(make: () => T): Tri<T> => ({ pt: make(), en: make(), es: make() });

export const emptyLandingQuiz = (): LandingQuiz => ({
  enabled: false,
  questions: [],
  rules: [],
  outcomes: {
    vip: { i18n: tri(() => ({ title: '', message: '' })) },
    qualified: { i18n: tri(() => ({ title: '', message: '' })) },
    nurture: { i18n: tri(() => ({ title: '', message: '' })) }
  },
  whatsappGroupUrl: '',
  nurtureSequence: []
});

/** Completa um quiz vindo da API (campos/idiomas ausentes) para o formato que o editor espera. */
export const hydrateLandingQuiz = (raw: any): LandingQuiz => {
  const base = emptyLandingQuiz();
  if (!raw) return base;
  const fill = <T extends object>(i18n: any, empty: () => T): Tri<T> => ({
    pt: { ...empty(), ...(i18n?.pt || {}) },
    en: { ...empty(), ...(i18n?.en || {}) },
    es: { ...empty(), ...(i18n?.es || {}) }
  });
  return {
    enabled: !!raw.enabled,
    whatsappGroupUrl: raw.whatsappGroupUrl || '',
    questions: (raw.questions || []).map((q: any) => ({
      key: q.key || '',
      i18n: fill(q.i18n, () => ({ text: '' })),
      options: (q.options || []).map((o: any) => ({ key: o.key || '', i18n: fill(o.i18n, () => ({ label: '' })) }))
    })),
    rules: (raw.rules || []).map((r: any) => ({
      outcome: r.outcome === 'vip' ? 'vip' : 'qualified',
      conditions: (r.conditions || []).map((c: any) => ({ questionKey: c.questionKey, optionKeys: c.optionKeys || [] }))
    })),
    outcomes: {
      vip: { i18n: fill(raw.outcomes?.vip?.i18n, () => ({ title: '', message: '' })) },
      qualified: { i18n: fill(raw.outcomes?.qualified?.i18n, () => ({ title: '', message: '' })) },
      nurture: { i18n: fill(raw.outcomes?.nurture?.i18n, () => ({ title: '', message: '' })) }
    },
    nurtureSequence: (raw.nurtureSequence || []).map((s: any) => ({
      delayDays: Number(s.delayDays) || 0,
      i18n: fill(s.i18n, () => ({ subject: '', bodyHtml: '' }))
    }))
  };
};

// Chave estável gerada só na criação (as regras e os leads guardados referenciam a chave).
const makeKey = (prefix: string, existing: string[]) => {
  let i = existing.length + 1;
  while (existing.includes(`${prefix}${i}`)) i += 1;
  return `${prefix}${i}`;
};

const move = <T,>(list: T[], index: number, delta: number): T[] => {
  const next = list.slice();
  const target = index + delta;
  if (target < 0 || target >= next.length) return list;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

interface Props {
  value: LandingQuiz;
  onChange: (next: LandingQuiz) => void;
}

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent';

const LandingQuizEditor: React.FC<Props> = ({ value, onChange }) => {
  const notifications = useNotifications();
  const [lang, setLang] = useState<QuizLang>('pt');
  const [loadingDefault, setLoadingDefault] = useState(false);
  const [translating, setTranslating] = useState(false);

  const set = (patch: Partial<LandingQuiz>) => onChange({ ...value, ...patch });
  const setQuestions = (questions: QuizQuestion[]) => {
    // Remove das regras referências a perguntas/opções que não existem mais.
    const valid = new Map(questions.map((q) => [q.key, new Set(q.options.map((o) => o.key))]));
    const rules = value.rules.map((r) => ({
      ...r,
      conditions: r.conditions
        .filter((c) => valid.has(c.questionKey))
        .map((c) => ({ ...c, optionKeys: c.optionKeys.filter((k) => valid.get(c.questionKey)!.has(k)) }))
    }));
    onChange({ ...value, questions, rules });
  };

  const handleLoadDefault = async () => {
    if (value.questions.length > 0 && !window.confirm('Substituir o quiz atual pelo modelo padrão?')) return;
    try {
      setLoadingDefault(true);
      const res = await axios.get('/landing-pages/quiz/default');
      onChange({ ...hydrateLandingQuiz(res.data), enabled: true, whatsappGroupUrl: value.whatsappGroupUrl });
      notifications.showSuccess('Modelo carregado', 'Revise as perguntas e as regras antes de salvar.');
    } catch (error: any) {
      notifications.showError('Erro ao carregar modelo', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setLoadingDefault(false);
    }
  };

  const handleTranslate = async () => {
    try {
      setTranslating(true);
      const res = await axios.post('/landing-pages/quiz/translate', { quiz: value });
      onChange(hydrateLandingQuiz({ ...res.data.quiz, enabled: value.enabled }));
      notifications.showSuccess('Tradução gerada!', 'Revise os textos em inglês e espanhol antes de salvar.');
    } catch (error: any) {
      notifications.showError('Erro ao traduzir', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setTranslating(false);
    }
  };

  // --- perguntas ---
  const patchQuestion = (qi: number, patch: Partial<QuizQuestion>) =>
    setQuestions(value.questions.map((q, i) => (i === qi ? { ...q, ...patch } : q)));
  const addQuestion = () =>
    setQuestions([
      ...value.questions,
      {
        key: makeKey('p', value.questions.map((q) => q.key)),
        i18n: tri(() => ({ text: '' })),
        options: [
          { key: 'o1', i18n: tri(() => ({ label: '' })) },
          { key: 'o2', i18n: tri(() => ({ label: '' })) }
        ]
      }
    ]);
  const addOption = (qi: number) => {
    const q = value.questions[qi];
    patchQuestion(qi, {
      options: [...q.options, { key: makeKey('o', q.options.map((o) => o.key)), i18n: tri(() => ({ label: '' })) }]
    });
  };

  // --- regras ---
  const setRules = (rules: QuizRule[]) => set({ rules });
  const toggleRuleOption = (ri: number, questionKey: string, optionKey: string) => {
    setRules(value.rules.map((r, i) => {
      if (i !== ri) return r;
      const existing = r.conditions.find((c) => c.questionKey === questionKey);
      const current = existing?.optionKeys || [];
      const optionKeys = current.includes(optionKey) ? current.filter((k) => k !== optionKey) : [...current, optionKey];
      const others = r.conditions.filter((c) => c.questionKey !== questionKey);
      return { ...r, conditions: optionKeys.length ? [...others, { questionKey, optionKeys }] : others };
    }));
  };

  const ptMissing = lang !== 'pt';

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-primary-600" /> Quiz de qualificação
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Com o quiz ativo, o CTA abre as perguntas e o link do grupo só é liberado para quem se qualificar.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
          <input
            type="checkbox"
            checked={value.enabled}
            onChange={(e) => set({ enabled: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Ativar quiz
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={handleLoadDefault} disabled={loadingDefault} className="btn-outline inline-flex items-center gap-1.5 text-sm px-3 py-1.5 disabled:opacity-50">
          <Wand2 className="w-4 h-4" /> {loadingDefault ? 'Carregando...' : 'Usar modelo padrão'}
        </button>
        <button type="button" onClick={handleTranslate} disabled={translating} className="btn-outline inline-flex items-center gap-1.5 text-sm px-3 py-1.5 disabled:opacity-50">
          <Sparkles className="w-4 h-4" /> {translating ? 'Traduzindo...' : 'Traduzir EN/ES com IA'}
        </button>
        <div className="ml-auto flex rounded-lg bg-gray-100 p-1">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => setLang(l.code)}
              className={`px-3 py-1 rounded-md text-xs font-medium ${lang === l.code ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
      {ptMissing && (
        <p className="text-xs text-gray-500 -mt-3">Campos vazios neste idioma usam o texto em português na página.</p>
      )}

      {/* Perguntas */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-gray-900">Perguntas</h4>
          <button type="button" onClick={addQuestion} className="text-sm text-primary-600 hover:underline inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> Pergunta
          </button>
        </div>
        {value.questions.length === 0 && <p className="text-sm text-gray-500">Nenhuma pergunta. Use o modelo padrão ou adicione.</p>}
        {value.questions.map((q, qi) => (
          <div key={q.key} className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 w-6">{qi + 1}.</span>
              <input
                type="text"
                value={q.i18n[lang].text}
                onChange={(e) => patchQuestion(qi, { i18n: { ...q.i18n, [lang]: { text: e.target.value } } })}
                placeholder={lang === 'pt' ? 'Texto da pergunta' : q.i18n.pt.text || 'Texto da pergunta'}
                className={inputCls}
              />
              <button type="button" onClick={() => setQuestions(move(value.questions, qi, -1))} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded" aria-label="Subir">
                <ArrowUp className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => setQuestions(move(value.questions, qi, 1))} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded" aria-label="Descer">
                <ArrowDown className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => setQuestions(value.questions.filter((_, i) => i !== qi))} className="p-1.5 text-red-600 hover:bg-red-50 rounded" aria-label="Remover pergunta">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="pl-8 space-y-2">
              {q.options.map((o, oi) => (
                <div key={o.key} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-gray-300 shrink-0" />
                  <input
                    type="text"
                    value={o.i18n[lang].label}
                    onChange={(e) => patchQuestion(qi, {
                      options: q.options.map((opt, i) => (i === oi ? { ...opt, i18n: { ...opt.i18n, [lang]: { label: e.target.value } } } : opt))
                    })}
                    placeholder={lang === 'pt' ? 'Opção' : o.i18n.pt.label || 'Opção'}
                    className={inputCls}
                  />
                  <button
                    type="button"
                    disabled={q.options.length <= 2}
                    onClick={() => patchQuestion(qi, { options: q.options.filter((_, i) => i !== oi) })}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-30"
                    aria-label="Remover opção"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => addOption(qi)} className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Opção
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Roteamento */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-gray-900">Roteamento</h4>
          <button
            type="button"
            onClick={() => setRules([...value.rules, { outcome: 'qualified', conditions: [] }])}
            className="text-sm text-primary-600 hover:underline inline-flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Regra
          </button>
        </div>
        <p className="text-xs text-gray-500">
          As regras são testadas em ordem e vale a primeira que bater. Numa regra, o lead precisa ter escolhido uma das opções
          marcadas em <strong>cada</strong> pergunta com opções marcadas. Perguntas sem nada marcado não contam. Quem não bater
          em nenhuma regra vai para a nutrição.
        </p>
        {value.rules.map((rule, ri) => (
          <div key={ri} className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-gray-500">Regra {ri + 1} →</span>
              <select
                value={rule.outcome}
                onChange={(e) => setRules(value.rules.map((r, i) => (i === ri ? { ...r, outcome: e.target.value as QuizRule['outcome'] } : r)))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value="vip">{OUTCOME_LABELS.vip}</option>
                <option value="qualified">{OUTCOME_LABELS.qualified}</option>
              </select>
              <div className="ml-auto flex items-center">
                <button type="button" onClick={() => setRules(move(value.rules, ri, -1))} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded" aria-label="Subir regra">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => setRules(move(value.rules, ri, 1))} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded" aria-label="Descer regra">
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => setRules(value.rules.filter((_, i) => i !== ri))} className="p-1.5 text-red-600 hover:bg-red-50 rounded" aria-label="Remover regra">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            {value.questions.map((q) => {
              const selected = rule.conditions.find((c) => c.questionKey === q.key)?.optionKeys || [];
              return (
                <div key={q.key}>
                  <p className="text-xs font-medium text-gray-700 mb-1">{q.i18n.pt.text || q.key}</p>
                  <div className="flex flex-wrap gap-2">
                    {q.options.map((o) => {
                      const on = selected.includes(o.key);
                      return (
                        <button
                          key={o.key}
                          type="button"
                          onClick={() => toggleRuleOption(ri, q.key, o.key)}
                          className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-primary-400'}`}
                        >
                          {o.i18n.pt.label || o.key}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {rule.conditions.length === 0 && <p className="text-xs text-amber-700">Marque pelo menos uma opção. Uma regra vazia nunca é aplicada.</p>}
          </div>
        ))}
      </div>

      {/* Resultados */}
      <div className="space-y-3">
        <h4 className="font-semibold text-gray-900">Tela de resultado</h4>
        {(['vip', 'qualified', 'nurture'] as QuizOutcome[]).map((outcome) => {
          const block = value.outcomes[outcome].i18n[lang];
          const patchOutcome = (field: 'title' | 'message', text: string) =>
            set({
              outcomes: {
                ...value.outcomes,
                [outcome]: { i18n: { ...value.outcomes[outcome].i18n, [lang]: { ...block, [field]: text } } }
              }
            });
          return (
            <div key={outcome} className="border border-gray-200 rounded-lg p-4 space-y-2">
              <p className="text-xs font-semibold text-gray-600">{OUTCOME_LABELS[outcome]}</p>
              <input type="text" value={block.title} onChange={(e) => patchOutcome('title', e.target.value)} placeholder="Título" className={inputCls} />
              <textarea value={block.message} onChange={(e) => patchOutcome('message', e.target.value)} placeholder="Mensagem" rows={2} className={inputCls} />
            </div>
          );
        })}
      </div>

      {/* Link dedicado */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Link do grupo para qualificados (opcional)</label>
        <input
          type="url"
          value={value.whatsappGroupUrl}
          onChange={(e) => set({ whatsappGroupUrl: e.target.value })}
          placeholder="https://chat.whatsapp.com/..."
          className={inputCls}
        />
        <p className="text-xs text-gray-500 mt-1">
          Vazio = usa o grupo do avatar. Um grupo exclusivo garante que o link não apareça em nenhuma página /a/.
        </p>
      </div>

      {/* Nutrição */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-gray-900">Sequência de nutrição (e-mails)</h4>
          <button
            type="button"
            onClick={() => {
              const last = value.nurtureSequence[value.nurtureSequence.length - 1];
              set({
                nurtureSequence: [
                  ...value.nurtureSequence,
                  { delayDays: last ? last.delayDays + 3 : 0, i18n: tri(() => ({ subject: '', bodyHtml: '' })) }
                ]
              });
            }}
            className="text-sm text-primary-600 hover:underline inline-flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> E-mail
          </button>
        </div>
        <p className="text-xs text-gray-500">
          Enviada a quem não se qualificar. Os dias contam a partir da resposta do quiz. Use {'{nome}'} para o primeiro nome.
          Todo e-mail sai com link para cancelar a inscrição.
        </p>
        {value.nurtureSequence.map((step, si) => {
          const block = step.i18n[lang];
          const patchStep = (patch: Partial<NurtureStep>) =>
            set({ nurtureSequence: value.nurtureSequence.map((s, i) => (i === si ? { ...s, ...patch } : s)) });
          return (
            <div key={si} className="border border-gray-200 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500">E-mail {si + 1} · D+</span>
                <input
                  type="number"
                  min={0}
                  max={365}
                  value={step.delayDays}
                  onChange={(e) => patchStep({ delayDays: Math.max(0, Number(e.target.value) || 0) })}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                />
                <span className="text-xs text-gray-500">dias</span>
                <button
                  type="button"
                  onClick={() => set({ nurtureSequence: value.nurtureSequence.filter((_, i) => i !== si) })}
                  className="ml-auto p-1.5 text-red-600 hover:bg-red-50 rounded"
                  aria-label="Remover e-mail"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <input
                type="text"
                value={block.subject}
                onChange={(e) => patchStep({ i18n: { ...step.i18n, [lang]: { ...block, subject: e.target.value } } })}
                placeholder="Assunto"
                className={inputCls}
              />
              <RichTextEditor
                key={`${si}-${lang}`}
                value={block.bodyHtml}
                onChange={(html) => patchStep({ i18n: { ...step.i18n, [lang]: { ...block, bodyHtml: html } } })}
                placeholder="Corpo do e-mail"
                minHeight="120px"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LandingQuizEditor;
